import React, { useCallback, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { colors, radii, spacing, typography } from '../../../core/theme/tokens';
import type { ImportLocalImage } from '../../../types/import';
import { filesFromDrop } from '../pickers/pickImportImages.web';
import { IMPORT_IMAGE_ACCEPT } from '../utils/imageHelpers';

type Props = {
  disabled?: boolean;
  onFiles: (images: ImportLocalImage[]) => void;
  onError: (message: string) => void;
};

type DragEventLike = {
  preventDefault: () => void;
  stopPropagation: () => void;
  dataTransfer?: { files?: ArrayLike<{ name: string; type: string; size: number }> };
};

/** Web-only drag-and-drop zone for bank screenshots. */
export function ImportDropZone({ disabled, onFiles, onError }: Props) {
  const [dragging, setDragging] = useState(false);

  const handleFiles = useCallback(
    async (list: ArrayLike<{ name: string; type: string; size: number }> | null | undefined) => {
      if (!list || disabled) return;
      try {
        const images = await filesFromDrop(list as Parameters<typeof filesFromDrop>[0]);
        onFiles(images);
      } catch (error) {
        onError(error instanceof Error ? error.message : 'Could not read files');
      }
    },
    [disabled, onError, onFiles],
  );

  return (
    <View
      {...({
        onDragEnter: (e: DragEventLike) => {
          e.preventDefault();
          e.stopPropagation();
          if (!disabled) setDragging(true);
        },
        onDragOver: (e: DragEventLike) => {
          e.preventDefault();
          e.stopPropagation();
          if (!disabled) setDragging(true);
        },
        onDragLeave: (e: DragEventLike) => {
          e.preventDefault();
          e.stopPropagation();
          setDragging(false);
        },
        onDrop: (e: DragEventLike) => {
          e.preventDefault();
          e.stopPropagation();
          setDragging(false);
          void handleFiles(e.dataTransfer?.files);
        },
      } as Record<string, unknown>)}
      style={[styles.zone, dragging && styles.zoneActive, disabled && styles.disabled]}
      accessibilityRole="button"
      accessibilityLabel="Drop bank screenshots or PDF statements here"
    >
      <Text style={styles.title}>{dragging ? 'Drop to add' : 'Drag files here'}</Text>
      <Text style={styles.subtitle}>JPEG, PNG, WebP, or PDF · up to 10MB each</Text>
      {React.createElement('input', {
        type: 'file',
        accept: IMPORT_IMAGE_ACCEPT,
        multiple: true,
        disabled,
        style: {
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          opacity: 0,
          cursor: 'pointer',
          width: '100%',
          height: '100%',
        },
        onChange: (e: { target: { files: ArrayLike<{ name: string; type: string; size: number }> | null; value: string } }) => {
          void handleFiles(e.target.files);
          e.target.value = '';
        },
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  zone: {
    position: 'relative',
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.glassBorder,
    borderRadius: radii.lg,
    paddingVertical: spacing.xl,
    paddingHorizontal: spacing.lg,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.03)',
    minHeight: 120,
    overflow: 'hidden',
  },
  zoneActive: {
    borderColor: colors.accent,
    backgroundColor: 'rgba(61, 214, 164, 0.08)',
  },
  disabled: {
    opacity: 0.5,
  },
  title: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
  },
  subtitle: {
    ...typography.caption,
    color: colors.textMuted,
    marginTop: spacing.xs,
  },
});
